import { useEffect, useState } from "react";
import {
  Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, MenuItem, Stack,
  TextField, Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { salesInvoicesApi, type PaymentLineRequest } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";

const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" });

const MODES: PaymentLineRequest["mode"][] = ["BankTransfer", "Cheque", "Card", "Cash", "Upi"];

/**
 * Recording what a payer actually settled (W19).
 *
 * <p>The patient pays at the counter; the payer pays weeks later, by bank transfer, usually for
 * less than was claimed. This is where the second one is written down — and without it the
 * insurance receivable the bill raised at posting would never clear.</p>
 *
 * <p><b>Short payment is expected.</b> A disallowed line, the payer's own tariff, a processing
 * deduction. So the dialog leads with the shortfall rather than hiding it: what is left after this
 * receipt is the number somebody has to chase or write off, and it should be visible at the moment
 * the decision to accept the remittance is made.</p>
 */
export function PayerSettlementDialog({
  invoiceId, documentNo, payerName, claimed, outstanding, open, onClose,
}: {
  invoiceId: string;
  documentNo: string;
  payerName: string;
  claimed: number;
  outstanding: number;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState<PaymentLineRequest["mode"]>("BankTransfer");
  const [reference, setReference] = useState("");

  useEffect(() => {
    if (!open) return;
    // Defaulted to the full claim: settling in full is the common case, and typing a figure that
    // is already on screen is how a transposition gets in.
    setAmount(String(outstanding));
    setMode("BankTransfer");
    setReference("");
  }, [open, outstanding]);

  const mutation = useMutation({
    meta: { successMessage: "Settlement recorded" },
    mutationFn: () => salesInvoicesApi.recordPayerSettlement(invoiceId, {
      amount: Number(amount || 0),
      mode,
      reference: reference.trim() || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sales-invoice", invoiceId] });
      onClose();
    },
  });

  const value = Number(amount || 0);
  const shortfall = outstanding - value;
  const err =
    amount === "" || Number.isNaN(value) ? "Enter the amount received"
    : value <= 0 ? "A settlement has to be for something — to write the claim off, raise a credit note"
    : value > outstanding
      ? `More than the ${inr.format(outstanding)} outstanding. That money belongs to another bill.`
    : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={(e) => { e.preventDefault(); if (!err) mutation.mutate(); }}>
        <ClosableDialogTitle onClose={onClose}>
          Claim settlement — {documentNo}
        </ClosableDialogTitle>
        <DialogContent dividers>
          {mutation.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>
          )}

          <Alert severity="info" sx={{ mb: 2 }}>
            <b>{payerName}</b> was claimed {inr.format(claimed)} on this bill, of which{" "}
            {inr.format(outstanding)} is outstanding. Recording a receipt clears that much of the
            insurance receivable.
          </Alert>

          <Stack spacing={2}>
            <Stack direction="row" spacing={2}>
              <TextField type="number" label="Amount received *" size="small" fullWidth autoFocus
                value={amount} onChange={(e) => setAmount(e.target.value)}
                inputProps={{ min: 0, step: "0.01" }} />
              <TextField select label="Mode" size="small" sx={{ minWidth: 170 }}
                value={mode}
                onChange={(e) => setMode(e.target.value as PaymentLineRequest["mode"])}>
                {MODES.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </TextField>
            </Stack>

            <TextField label="Remittance reference" size="small" fullWidth
              value={reference} onChange={(e) => setReference(e.target.value)}
              inputProps={{ maxLength: 100 }}
              helperText="The advice number or UTR — what reconciles this to a bank statement" />

            {!err && shortfall > 0 && (
              <Alert severity="warning">
                <b>{inr.format(shortfall)} short of the claim.</b> The balance stays outstanding
                against {payerName} — chase it, or raise a credit note to write it off. It does not
                move to the patient.
              </Alert>
            )}
            {!err && shortfall === 0 && (
              <Typography variant="caption" color="success.main">
                Settles the claim in full.
              </Typography>
            )}
            {err && <Alert severity="info">{err}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={!!err || mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : null}>
            Record settlement
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
